import { useEffect, useMemo, useState } from 'react';
import {
  topLevelFeatures,
  childrenOf,
  ROLES,
  type FeatureId,
  type FeatureNode,
  type Role,
} from '@sequesto-os/shared';
import { Modal } from '../../components/ui/Modal';
import { Button } from '../../components/ui/Button';
import { Alert } from '../../components/ui/Alert';
import { LoadingState } from '../../components/ui/States';
import {
  useUserAccess,
  useUpdateUserAccess,
  useRoleDefaults,
  useUpdateRoleDefaults,
} from '../../hooks/useUsers';
import { useTeams } from '../../hooks/useTeams';
import type { AccessOverride, RoleDefaults, User } from '../../api/users';

type OverrideState = Record<string, AccessOverride>;
type TriValue = 'default' | 'allow' | 'deny';

/** Flatten the catalog into [parent, ...children] order for rendering rows. */
export function orderedFeatures(): FeatureNode[] {
  const rows: FeatureNode[] = [];
  for (const parent of topLevelFeatures()) {
    rows.push(parent);
    rows.push(...childrenOf(parent.id as FeatureId));
  }
  return rows;
}

function TriToggle({
  value,
  onChange,
  disabled,
}: {
  value: TriValue;
  onChange: (v: TriValue) => void;
  disabled?: boolean;
}) {
  const options: Array<{ id: TriValue; label: string }> = [
    { id: 'default', label: 'Default' },
    { id: 'allow', label: 'Allow' },
    { id: 'deny', label: 'Deny' },
  ];
  return (
    <div className="inline-flex rounded-md border border-border bg-surface-primary p-0.5">
      {options.map((opt) => (
        <button
          key={opt.id}
          type="button"
          disabled={disabled}
          onClick={() => onChange(opt.id)}
          className={`rounded px-2.5 py-1 text-body-xs transition-colors disabled:opacity-40 ${
            value === opt.id
              ? 'bg-surface-inverse text-text-inverse'
              : 'text-text-secondary hover:text-text-primary'
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

/** Per-user override editor. Default inherits role + teams; Allow/Deny override it. */
export function UserAccessModal({ user, onClose }: { user: User; onClose: () => void }) {
  const { data, isLoading } = useUserAccess(user.id);
  const { data: roleData } = useRoleDefaults();
  const { data: teamsData } = useTeams();
  const update = useUpdateUserAccess();

  const [overrides, setOverrides] = useState<OverrideState>({});
  const [teamIds, setTeamIds] = useState<string[]>([]);

  useEffect(() => {
    if (data) {
      setOverrides(data.overrides);
      setTeamIds(data.teamIds);
    }
  }, [data]);

  const roleDefaults = roleData?.defaults[user.role] ?? {};
  const allTeams = teamsData?.teams ?? [];
  const rows = useMemo(orderedFeatures, []);
  const isAdminUser = user.role === 'admin';

  // Features granted by the teams this user is (currently) assigned to.
  const teamGranted = useMemo(() => {
    const set = new Set<string>();
    for (const t of allTeams) {
      if (teamIds.includes(t.id)) t.features.forEach((f) => set.add(f));
    }
    return set;
  }, [allTeams, teamIds]);

  const setTri = (feature: string, v: TriValue) => {
    setOverrides((prev) => {
      const next = { ...prev };
      if (v === 'default') delete next[feature];
      else next[feature] = v;
      return next;
    });
  };

  const toggleTeam = (id: string) => {
    setTeamIds((prev) => (prev.includes(id) ? prev.filter((t) => t !== id) : [...prev, id]));
  };

  const valueFor = (feature: string): TriValue => overrides[feature] ?? 'default';

  /** What "Default" resolves to, factoring in role default + team grants. */
  const defaultSource = (f: FeatureNode): string => {
    if (f.alwaysAllowed) return 'Always available';
    const byRole = roleDefaults[f.id] === true;
    const byTeam = teamGranted.has(f.id);
    if (byRole && byTeam) return 'Default: Allowed (role + team)';
    if (byTeam) return 'Default: Allowed (via team)';
    if (byRole) return 'Default: Allowed (role)';
    return 'Default: Blocked';
  };

  return (
    <Modal isOpen onClose={onClose} title={`Access — ${user.name}`} size="full">
      {isLoading ? (
        <LoadingState />
      ) : (
        <div className="space-y-4">
          {isAdminUser && (
            <Alert variant="info">
              Admins always have full access. Teams and overrides are ignored while this user is an
              admin.
            </Alert>
          )}

          <div>
            <p className="text-body-sm font-medium text-text-primary mb-2">Teams</p>
            {allTeams.length === 0 ? (
              <p className="text-body-xs text-text-tertiary">
                No teams yet. Create teams from the Teams button to grant access in bundles.
              </p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {allTeams.map((t) => {
                  const selected = teamIds.includes(t.id);
                  return (
                    <button
                      key={t.id}
                      type="button"
                      disabled={isAdminUser}
                      onClick={() => toggleTeam(t.id)}
                      className={`rounded-full border px-3 py-1 text-body-xs transition-colors disabled:opacity-40 ${
                        selected
                          ? 'border-transparent bg-surface-inverse text-text-inverse'
                          : 'border-border text-text-secondary hover:text-text-primary'
                      }`}
                      title={t.features.join(', ') || 'No features'}
                    >
                      {t.name}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <p className="text-body-sm text-text-tertiary">
            “Default” inherits the <span className="capitalize">{user.role}</span> role plus the
            selected teams. Use Allow or Deny to override a single area for this person.
          </p>

          <div className="max-h-[45vh] overflow-y-auto divide-y divide-border-light">
            {rows.map((f) => {
              const isChild = !!f.parentId;
              return (
                <div
                  key={f.id}
                  className={`flex items-center justify-between py-2.5 ${isChild ? 'pl-6' : ''}`}
                >
                  <div>
                    <p className="text-body-sm text-text-primary">{f.label}</p>
                    <p className="text-body-xs text-text-tertiary">{defaultSource(f)}</p>
                  </div>
                  <TriToggle
                    value={f.alwaysAllowed ? 'default' : valueFor(f.id)}
                    onChange={(v) => setTri(f.id, v)}
                    disabled={f.alwaysAllowed || isAdminUser}
                  />
                </div>
              );
            })}
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-border-light">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={update.isPending}
              onClick={() =>
                update.mutate({ id: user.id, overrides, teamIds }, { onSuccess: onClose })
              }
            >
              {update.isPending ? 'Saving...' : 'Save Access'}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}

/** Per-role default editor. Admin column is read-only (admins always have full access). */
export function RoleDefaultsModal({ onClose }: { onClose: () => void }) {
  const { data, isLoading } = useRoleDefaults();
  const update = useUpdateRoleDefaults();

  const [defaults, setDefaults] = useState<RoleDefaults>({});

  useEffect(() => {
    if (data) setDefaults(data.defaults);
  }, [data]);

  const rows = useMemo(orderedFeatures, []);

  const isAllowed = (role: Role, feature: FeatureNode): boolean => {
    if (role === 'admin' || feature.alwaysAllowed) return true;
    return defaults[role]?.[feature.id] === true;
  };

  const toggle = (role: Role, feature: string) => {
    setDefaults((prev) => ({
      ...prev,
      [role]: { ...(prev[role] ?? {}), [feature]: !(prev[role]?.[feature] === true) },
    }));
  };

  const handleSave = () => {
    const entries: Array<{ role: Role; feature: string; allowed: boolean }> = [];
    for (const role of ROLES) {
      if (role === 'admin') continue; // admins bypass; nothing to persist
      for (const f of rows) {
        if (f.alwaysAllowed) continue;
        entries.push({ role, feature: f.id, allowed: defaults[role]?.[f.id] === true });
      }
    }
    update.mutate(entries, { onSuccess: onClose });
  };

  return (
    <Modal isOpen onClose={onClose} title="Role access defaults" size="full">
      {isLoading ? (
        <LoadingState />
      ) : (
        <div className="space-y-4">
          <p className="text-body-sm text-text-tertiary">
            What each role can see by default. Per-person grants and limits are set from each user's
            Access editor. Admins always have full access.
          </p>

          <div className="max-h-[55vh] overflow-y-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border-light">
                  <th className="text-left py-2 px-2 text-body-xs font-medium text-text-tertiary">
                    Area
                  </th>
                  {ROLES.map((r) => (
                    <th
                      key={r}
                      className="text-center py-2 px-2 text-body-xs font-medium text-text-tertiary"
                    >
                      {r}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((f) => (
                  <tr key={f.id} className="border-b border-border-light last:border-0">
                    <td className={`py-2 px-2 text-body-sm text-text-primary ${f.parentId ? 'pl-6' : ''}`}>
                      {f.label}
                    </td>
                    {ROLES.map((r) => {
                      const locked = r === 'admin' || f.alwaysAllowed;
                      return (
                        <td key={r} className="text-center py-2 px-2">
                          <input
                            type="checkbox"
                            checked={isAllowed(r, f)}
                            disabled={locked}
                            onChange={() => toggle(r, f.id)}
                            className="h-4 w-4 rounded border-border accent-text-primary disabled:opacity-40"
                          />
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-border-light">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button variant="primary" disabled={update.isPending} onClick={handleSave}>
              {update.isPending ? 'Saving...' : 'Save Defaults'}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}
